import React, { useState } from 'react';
import { PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';
import { DraggableModal } from '../../components/ui/DraggableModal';
import devopsBoardService, { TeamMember } from '../../services/devopsBoard.service';
import {
  attachmentTooLarge,
  imageFilesFromClipboard,
} from './newWorkItemAttachments';
import { plainTextToHtml } from './devopsDescription';

/**
 * Raise a new work item from the board.
 *
 * Two shapes: a story or bug for the sprint, or a task under a specific story. When a
 * parent is given the type is fixed to Task — a story cannot hang off another story
 * here, and offering the choice would only invite a broken hierarchy.
 *
 * Screenshots can be pasted straight in, or files picked; they upload after the item
 * is created — DevOps can only attach to an item that already exists.
 */

/** Human-readable file size for the queued-attachment rows. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
  /** Sprint the item lands in. */
  iterationPath: string;
  /** Set when adding a task beneath a story row. */
  parent?: { id: number; title: string };
  members: TeamMember[];
  onClose: () => void;
  onCreated: () => void;
}

const NewWorkItemModal: React.FC<Props> = ({
  iterationPath, parent, members, onClose, onCreated,
}) => {
  const isTask = Boolean(parent);
  const [workItemType, setWorkItemType] =
    useState<'User Story' | 'Bug' | 'Task'>(isTask ? 'Task' : 'User Story');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  // Set once the item exists in DevOps: from then on, Create must not run again —
  // retrying a failed attachment by re-clicking would raise a duplicate work item.
  const [createdId, setCreatedId] = useState<number | null>(null);

  const queueFiles = (incoming: File[]) => {
    const tooLarge = incoming.find(attachmentTooLarge);
    if (tooLarge) {
      setError(`${tooLarge.name} is too large to attach (25 MB limit).`);
      return;
    }
    setError(null);
    setFiles(f => [...f, ...incoming]);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    if (createdId) return;
    const startAt = files.filter(f => /^screenshot-\d/.test(f.name)).length + 1;
    const pasted = imageFilesFromClipboard(e.clipboardData.items, startAt);
    if (pasted.length === 0) return;
    // Text pastes into the field as usual; an image paste must not also insert junk.
    e.preventDefault();
    queueFiles(pasted);
  };

  const create = async () => {
    const trimmed = title.trim();
    if (!trimmed || createdId) return;
    setSaving(true);
    setError(null);

    let id: number;
    try {
      const body = description.trim();
      const created = await devopsBoardService.createWorkItem({
        workItemType,
        title: trimmed,
        iterationPath,
        parentId: parent?.id,
        assignedTo: assignedTo || undefined,
        description: body ? plainTextToHtml(body) : undefined,
      });
      id = created.id;
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'That work item could not be created.');
      setSaving(false);
      return;
    }

    // Attachments ride behind the create; a failure here must not look like a failed
    // create — the item exists, and re-creating it would duplicate it.
    const failed: string[] = [];
    for (const f of files) {
      try {
        await devopsBoardService.addAttachment(id, f);
      } catch {
        failed.push(f.name);
      }
    }
    setSaving(false);
    onCreated();
    if (failed.length === 0) {
      onClose();
      return;
    }
    setCreatedId(id);
    setError(
      `Work item ${id} was created, but ${failed.join(', ')} could not be attached. ` +
      `Open its card to attach ${failed.length === 1 ? 'it' : 'them'} again.`,
    );
  };

  const label = 'block text-xs font-medium text-muted mb-0.5';
  const field = `w-full rounded border border-gray-300 dark:border-gray-600 bg-white
                 dark:bg-gray-900 px-2 py-1 text-sm text-gray-900 dark:text-gray-100`;

  return (
    <DraggableModal
      isOpen
      onClose={onClose}
      title={<span className="text-sm font-semibold">
        {isTask ? `New task under ${parent!.id}` : 'New work item'}
      </span>}
      width={520}
    >
      <div className="space-y-3 p-4" onPaste={onPaste}>
        {error && (
          <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm
                          text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200">
            {error}
          </div>
        )}

        {isTask && (
          <p className="text-xs text-muted">
            Will be linked to <strong>{parent!.title}</strong>.
          </p>
        )}

        {!isTask && (
          <div>
            <label className={label} htmlFor="nwi-type">Type</label>
            <select
              id="nwi-type"
              className={field}
              value={workItemType}
              onChange={e => setWorkItemType(e.target.value as 'User Story' | 'Bug')}
            >
              <option value="User Story">User Story</option>
              <option value="Bug">Bug</option>
            </select>
          </div>
        )}

        <div>
          <label className={label} htmlFor="nwi-title">Title</label>
          <input
            id="nwi-title"
            className={field}
            value={title}
            autoFocus
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') create(); }}
            placeholder="What needs doing?"
          />
        </div>

        <div>
          {/* A Bug's body lands in Repro Steps (and shows there on its card), so the
              label says where the text will actually end up. */}
          <label className={label} htmlFor="nwi-description">
            {workItemType === 'Bug' ? 'Repro Steps' : 'Description'}
          </label>
          <textarea
            id="nwi-description"
            className={field}
            rows={4}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional — what, why, how to reproduce…"
          />
        </div>

        <div>
          <label className={label} htmlFor="nwi-assignee">Assigned to</label>
          <select
            id="nwi-assignee"
            className={field}
            value={assignedTo}
            onChange={e => setAssignedTo(e.target.value)}
          >
            <option value="">Unassigned</option>
            {members.map(m => <option key={m.email} value={m.email}>{m.name}</option>)}
          </select>
        </div>

        <div>
          <span className={label}>Attachments</span>
          {files.length > 0 && (
            <div className="mb-2 space-y-1 rounded border border-gray-200 p-2 dark:border-gray-700">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs">
                  <PaperClipIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span className="truncate text-gray-700 dark:text-gray-200">{f.name}</span>
                  <span className="shrink-0 text-gray-400">{formatSize(f.size)}</span>
                  {!createdId && (
                    <button
                      className="ml-auto shrink-0 text-gray-400 hover:text-red-600"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles(list => list.filter((_, j) => j !== i))}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <input
            type="file"
            id="nwi-attach"
            className="hidden"
            multiple
            disabled={Boolean(createdId)}
            onChange={e => {
              const picked = Array.from(e.target.files ?? []);
              // Reset the input so re-picking the same file fires onChange again.
              e.target.value = '';
              if (picked.length) queueFiles(picked);
            }}
          />
          <label
            htmlFor="nwi-attach"
            className="inline-flex cursor-pointer items-center gap-1 rounded border
                       border-gray-300 px-2 py-1 text-xs text-gray-700
                       dark:border-gray-600 dark:text-gray-200"
          >
            <PaperClipIcon className="h-3.5 w-3.5" />
            Attach a file
          </label>
          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
            or paste a screenshot (Ctrl+V / ⌘V)
          </span>
        </div>

        <p className="text-xs text-gray-400 dark:text-gray-500">
          Lands in {iterationPath.split('\\').pop()}. It starts in New.
        </p>

        <div className="flex justify-end gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
          <button
            className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700
                       dark:border-gray-600 dark:text-gray-200"
            onClick={onClose}
          >
            {createdId ? 'Close' : 'Cancel'}
          </button>
          <button
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50"
            disabled={saving || !title.trim() || Boolean(createdId)}
            onClick={create}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </DraggableModal>
  );
};

export default NewWorkItemModal;
